import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import * as client from '../lib/api';
import { connect, disconnect } from '../lib/realtime';
import type { CurrentUser, Org, Permission } from '../lib/types';

interface AuthState {
  user: CurrentUser | null;
  org: Org | null;
  isDemo: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  reload: () => Promise<void>;
  /**
   * Mirrors the server's permission check. It decides what to render; the API
   * decides what actually happens. Never treat a `true` here as authorisation.
   */
  can: (...permissions: Permission[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [org, setOrg] = useState<Org | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const me = await client.fetchMe();
    setUser(me.user);
    setOrg(me.org);
    setIsDemo(me.isDemo);
    void connect();
  }, []);

  // On start-up the page has only the httpOnly refresh cookie. Exchanging it
  // here is what makes a reload feel like a continuing shift rather than a new
  // sign-in — which matters when the reload was a browser crash mid-call.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const restored = await client.refreshSession();
      if (cancelled) return;
      if (restored) {
        await load().catch(() => client.setAccessToken(null));
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const signedIn = await client.login(email, password);
      setUser(signedIn);
      await load();
    },
    [load],
  );

  const signOut = useCallback(async () => {
    disconnect();
    await client.logout();
    setUser(null);
    setOrg(null);
  }, []);

  const can = useCallback(
    (...permissions: Permission[]) =>
      Boolean(user) && permissions.every((permission) => user!.permissions.includes(permission)),
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({ user, org, isDemo, loading, signIn, signOut, reload: load, can }),
    [user, org, isDemo, loading, signIn, signOut, load, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}

/** The console a role lands on after signing in. */
export function homeRouteFor(user: CurrentUser): string {
  if (user.role === 'call_taker') return user.canTakeCalls ? '/calls' : '/incidents';
  if (user.role === 'dispatcher') return '/dispatch';
  return '/admin';
}
